import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { socialMediaAPI } from '../services/platformAPIs';
import { FacebookAPI, LinkedInAPI, TwitterAPI } from '../services/platformAPIs';

const SocialMediaOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Przetwarzanie autoryzacji...');

  useEffect(() => {
    handleOAuthCallback();
  }, []);

  const handleOAuthCallback = async () => {
    try {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const platform = searchParams.get('platform') || state?.split('-')[0];

      // Check for errors from OAuth provider
      if (error) {
        throw new Error(`Błąd autoryzacji: ${searchParams.get('error_description') || error}`);
      }

      if (!code || !platform) {
        throw new Error('Brak wymaganych parametrów autoryzacji');
      }

      setMessage(`Łączenie z ${platform}...`);

      let token: string;
      let additionalData: any = {};

      // Exchange code for token based on platform
      switch (platform.toLowerCase()) {
        case 'facebook':
          token = await FacebookAPI.exchangeCodeForToken(
            code, 
            `${window.location.origin}/social-media/oauth/callback?platform=facebook`
          );
          
          // Get Facebook pages
          const fbApi = new FacebookAPI(token);
          const pages = await fbApi.getPages();
          additionalData = { pages };
          break;

        case 'linkedin':
          token = await LinkedInAPI.exchangeCodeForToken(
            code,
            `${window.location.origin}/social-media/oauth/callback?platform=linkedin`
          );
          
          // Pobierz profil przez backend proxy
          try {
            const linkedInApi = new LinkedInAPI(token);
            const profile = await linkedInApi.getProfile();
            additionalData = { profile };
          } catch (profileError) {
            console.warn('Could not fetch LinkedIn profile:', profileError);
            // Zapisz token nawet jeśli profil się nie pobrał
            additionalData = { token_saved: true };
          }
          break;

        case 'twitter':
          // Twitter uses different flow (PKCE)
          // This would need code verifier from session storage
          const codeVerifier = sessionStorage.getItem('twitter_code_verifier');
          if (!codeVerifier) {
            throw new Error('Brak code verifier dla Twitter');
          }
          
          // Exchange code for token
          const response = await fetch('https://api.twitter.com/2/oauth2/token', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              grant_type: 'authorization_code',
              code,
              redirect_uri: `${window.location.origin}/social-media/oauth/callback?platform=twitter`,
              code_verifier: codeVerifier,
              client_id: import.meta.env.VITE_TWITTER_CLIENT_ID || '',
            }),
          });
          
          const data = await response.json();
          token = data.access_token;
          
          // Clear code verifier
          sessionStorage.removeItem('twitter_code_verifier');
          break;

        default:
          throw new Error(`Nieobsługiwana platforma: ${platform}`);
      }

      // Save token to database
      await socialMediaAPI.saveToken(platform.toLowerCase(), token, additionalData);

      setStatus('success');
      setMessage(`Pomyślnie połączono z ${platform}!`);

      // Redirect back to social media settings after 2 seconds
      setTimeout(() => {
        navigate('/social-media?view=settings');
      }, 2000);

    } catch (error) {
      console.error('OAuth callback error:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Wystąpił błąd podczas autoryzacji');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900">
      <div className="bg-zinc-800 rounded-2xl p-8 shadow-2xl border border-zinc-700 max-w-md w-full">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Autoryzacja w toku</h2>
              <p className="text-gray-400">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Sukces!</h2>
              <p className="text-gray-400">{message}</p>
              <p className="text-sm text-gray-500 mt-2">Przekierowanie za 2 sekundy...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Błąd autoryzacji</h2>
              <p className="text-gray-400">{message}</p>
              <button
                onClick={() => navigate('/social-media?view=settings')}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
              >
                Wróć do ustawień
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialMediaOAuthCallback;
